import i18n from "i18next";
import { toast } from "sonner";
import { logWarning } from "web_myplann/Infrastructure/sentry";
import { Resources } from "web_myplann/Infrastructure/Translations/Resources";
import { AppRouting } from "web_myplann/Infrastructure/Utils/UrlUtils";
import { EventSourceEnum } from "web_myplann/NotificationCenter/EventAlert/types";
import { buildContractEventRoute } from "web_myplann/NotificationCenter/Utils/buildContractEventRoute";
import type { NavigationPayload } from "web_myplann/Utils/WebViewHandlers/Types/NotificationNavigationTypes";
import {
	type NavigationDestination,
	NavigationDestinationType,
	normalizeNavigationDestination,
} from "web_myplann/Utils/WebViewModel";

const NO_DEEP_LINK_GROUPS = new Set(["newevent", "silent", "old"]);

export const isNoDeepLinkGroup = (type: string | undefined): boolean =>
	!!type && NO_DEEP_LINK_GROUPS.has(type.trim().toLowerCase());

export type NavigateFn = (to: string) => Promise<void> | void;

type DestinationRoute = {
	path: (sourceId: string, payload: NavigationPayload) => string;
	listRoute?: () => string;
};

const byId =
	(buildPath: (sourceId: string) => string): DestinationRoute["path"] =>
	(sourceId) =>
		buildPath(sourceId);

const DESTINATION_ROUTES: Partial<
	Record<NavigationDestination, DestinationRoute>
> = {
	activity: {
		path: byId(AppRouting.MyPlann.ActivityDetail.path),
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	activityTerm: {
		path: byId(AppRouting.MyPlann.ActivityDetail.path),
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	activityTermNotice: {
		path: byId(AppRouting.MyPlann.ActivityDetail.path),
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	task: {
		path: byId(AppRouting.MyPlann.TaskDetail.path),
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	taskTerm: {
		path: byId(AppRouting.MyPlann.TaskDetail.path),
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	taskTermNotice: {
		path: byId(AppRouting.MyPlann.TaskDetail.path),
		listRoute: AppRouting.MyPlann.ActivitiesList.path,
	},
	chat: {
		path: byId(AppRouting.MyPlann.Chat.Detail.path),
	},
	portfolio: {
		path: (sourceId, payload) =>
			buildContractEventRoute({
				portfolioId: sourceId,
				eventSource: EventSourceEnum.NotificationCenter,
				eventType: payload.eventType,
				eventDate: payload.eventDate,
			}),
	},
};

export const isPortfolioDestination = (type: string | undefined): boolean =>
	normalizeNavigationDestination(type) === NavigationDestinationType.portfolio;

const PORTFOLIO_ID_PREFIX = /^(\d+)(?:-|$)/;

const parsePortfolioIdFromSourceId = (
	sourceId: string | undefined,
): string | undefined => sourceId?.match(PORTFOLIO_ID_PREFIX)?.[1];

export const resolveSourceId = (
	payload: NavigationPayload,
): string | undefined =>
	isPortfolioDestination(payload.type)
		? payload.portfolioId || parsePortfolioIdFromSourceId(payload.sourceId)
		: payload.sourceId;

export const isReminderDestination = (
	type: string | undefined,
	notificationType: string | undefined,
): boolean =>
	normalizeNavigationDestination(type) === NavigationDestinationType.reminder ||
	notificationType?.toLowerCase() === "reminderterm";

export const isInvestmentQuestionnaireDestination = (
	type: string | undefined,
	notificationType: string | undefined,
): boolean =>
	normalizeNavigationDestination(type) ===
		NavigationDestinationType.investmentQuestionnaire ||
	notificationType?.toLowerCase() === "investmentquestionnaire";

export const isWebDestination = (type: string | undefined): boolean =>
	normalizeNavigationDestination(type) !== undefined;

export const getDestinationRoute = (type: string | undefined) => {
	const destination = normalizeNavigationDestination(type);
	return destination ? DESTINATION_ROUTES[destination] : undefined;
};

const logNavigationIssue = (reason: string, payload: NavigationPayload) =>
	logWarning(reason, {
		notification_type: payload.type,
		notification_id: payload.id,
		notification_source_id: payload.sourceId,
	});

export const fallbackToNotificationList = (
	reason: string,
	payload: NavigationPayload,
	navigate: NavigateFn,
) => {
	logNavigationIssue(reason, payload);
	toast.error(i18n.t(Resources.Common.somethingWentWrong));
	return navigate(AppRouting.MyPlann.Notifications.path());
};

export const navigateToSource = async (
	destination: DestinationRoute,
	payload: NavigationPayload,
	navigate: NavigateFn,
	getCurrentPath: () => string,
) => {
	const sourceId = resolveSourceId(payload);
	if (!sourceId) {
		if (isPortfolioDestination(payload.type)) {
			logNavigationIssue(
				"Portfolio notification navigation without portfolioId",
				payload,
			);
			await navigate(AppRouting.MyPlann.Notifications.path());
			return;
		}
		await fallbackToNotificationList(
			"Notification navigation without sourceId",
			payload,
			navigate,
		);
		return;
	}

	if (destination.listRoute) {
		const listPath = destination.listRoute();
		if (getCurrentPath() !== listPath) {
			await navigate(listPath);
		}
	}

	await navigate(destination.path(sourceId, payload));
};
